import React, { useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

interface QueueMetric {
  timestamp: string;
  queueDepth: number;
  processingRate: number;
  failureRate: number;
}

interface QueueMetricsChartProps {
  metrics: QueueMetric[];
  timeRange: '5m' | '15m' | '1h' | '6h';
  onTimeRangeChange: (range: '5m' | '15m' | '1h' | '6h') => void;
}

export const QueueMetricsChart: React.FC<QueueMetricsChartProps> = ({ 
  metrics, 
  timeRange, 
  onTimeRangeChange 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || metrics.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate scales
    const padding = 40;
    const chartWidth = canvas.width - padding * 2;
    const chartHeight = canvas.height - padding * 2;

    const maxDepth = Math.max(...metrics.map(m => m.queueDepth || 0));
    const maxRate = Math.max(...metrics.map(m => m.processingRate || 0));

    // Draw grid
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;

    // Horizontal grid lines
    for (let i = 0; i <= 5; i++) {
      const y = padding + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(canvas.width - padding, y);
      ctx.stroke();
    }

    // Draw axes
    ctx.strokeStyle = '#9ca3af';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, canvas.height - padding);
    ctx.lineTo(canvas.width - padding, canvas.height - padding);
    ctx.stroke();

    // Draw queue depth line
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.beginPath();
    metrics.forEach((metric, i) => {
      const x = padding + (chartWidth / (metrics.length - 1)) * i;
      const y = canvas.height - padding - ((metric.queueDepth || 0) / maxDepth) * chartHeight;
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();

    // Draw processing rate line
    ctx.strokeStyle = '#10b981';
    ctx.lineWidth = 2;
    ctx.beginPath();
    metrics.forEach((metric, i) => {
      const x = padding + (chartWidth / (metrics.length - 1)) * i;
      const y = canvas.height - padding - ((metric.processingRate || 0) / maxRate) * chartHeight;
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();

    // Draw labels
    ctx.fillStyle = '#374151';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'right';

    // Y-axis labels for queue depth
    for (let i = 0; i <= 5; i++) {
      const value = (maxDepth / 5) * (5 - i);
      const y = padding + (chartHeight / 5) * i;
      ctx.fillText(value.toFixed(0), padding - 10, y + 4);
    }

    // Time labels
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const timeLabels = getTimeLabels(metrics, 5);
    timeLabels.forEach((label, i) => {
      const x = padding + (chartWidth / (timeLabels.length - 1)) * i;
      ctx.fillText(label, x, canvas.height - padding + 10);
    });

    // Legend
    ctx.textAlign = 'left';
    ctx.fillStyle = '#3b82f6';
    ctx.fillRect(canvas.width - 150, 20, 15, 15);
    ctx.fillStyle = '#374151';
    ctx.fillText('Queue Depth', canvas.width - 130, 32);

    ctx.fillStyle = '#10b981';
    ctx.fillRect(canvas.width - 150, 45, 15, 15);
    ctx.fillStyle = '#374151';
    ctx.fillText('Processing Rate', canvas.width - 130, 57);

  }, [metrics]);

  const getTimeLabels = (metrics: QueueMetric[], count: number): string[] => {
    if (metrics.length === 0) return [];
    
    const labels: string[] = [];
    const step = Math.floor(metrics.length / count);
    
    for (let i = 0; i < metrics.length; i += step) {
      const date = new Date(metrics[i].timestamp);
      labels.push(date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }));
    }
    
    return labels;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Queue Metrics Trend</CardTitle>
          <div className="flex space-x-2">
            {(['5m', '15m', '1h', '6h'] as const).map((range) => (
              <button
                key={range}
                onClick={() => onTimeRangeChange(range)}
                className={`px-3 py-1 text-sm rounded ${
                  timeRange === range
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative h-64">
          <canvas 
            ref={canvasRef} 
            className="w-full h-full"
            style={{ display: 'block' }}
          />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <p className="text-gray-500">Current Depth</p>
            <p className="font-semibold text-lg">
              {metrics.length > 0 ? (metrics[metrics.length - 1].queueDepth || 0).toLocaleString() : '0'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-gray-500">Avg Processing Rate</p>
            <p className="font-semibold text-lg">
              {metrics.length > 0 
                ? (metrics.reduce((sum, m) => sum + (m.processingRate || 0), 0) / metrics.length).toFixed(1) 
                : '0'}/s
            </p>
          </div>
          <div className="text-center">
            <p className="text-gray-500">Avg Failure Rate</p>
            <p className="font-semibold text-lg">
              {metrics.length > 0 
                ? (metrics.reduce((sum, m) => sum + (m.failureRate || 0), 0) / metrics.length).toFixed(1) 
                : '0'}%
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};